import { useCallback, useEffect, useMemo, useRef } from 'react';

class DebounceCancelError extends Error {
  type = 'DebounceCancelError';

  message = 'Debounced function was cancelled';
}

export const isDebounceCancelError = (error: unknown): error is DebounceCancelError =>
  error instanceof DebounceCancelError;

export const useDebouncedCallbackWithPromise = <CB extends (...args: any[]) => Promise<any>>(
  callback: CB,
  delay: number
) => {
  const timer = useRef<NodeJS.Timeout>();
  const rejects = useRef<((reason?: any) => void)[]>([]);

  const cancelCalls = useCallback(() => {
    clearTimeout(timer.current);
    const rejectsCopy = [...rejects.current];
    rejects.current = [];
    rejectsCopy.forEach((reject) => reject(new DebounceCancelError()));
  }, []);

  const debouncedCallback = useMemo(
    () =>
      (...args: Parameters<CB>): Promise<ReturnType<CB>> => {
        cancelCalls();
        return new Promise((resolve, reject) => {
          rejects.current.push(reject);
          timer.current = setTimeout(() => {
            resolve(callback(...args));
          }, delay);
        });
      },
    [callback, cancelCalls, delay]
  );

  useEffect(() => cancelCalls, [cancelCalls]);

  return debouncedCallback;
};
